import { useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import Sidebar from "@/components/app/Sidebar";
import OnboardingWizard from "@/components/app/OnboardingWizard";
import InactivityBanner from "@/components/app/InactivityBanner";
import PaywallModal from "@/components/app/PaywallModal";
import UsageTimerBanner from "@/components/app/UsageTimerBanner";
import { useAuth } from "@/lib/auth";
import { useUsageTimer } from "@/lib/useUsageTimer";
import { Menu, X } from "lucide-react";

export default function DashboardLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [forcePaywall, setForcePaywall] = useState(false);
  const location = useLocation();
  const params = useParams();
  const { user } = useAuth();
  const { timer, frozen } = useUsageTimer();

  // Onboarding gate
  const needsOnboarding = user && user.role === "student" && user.onboarded === false;

  // Project id from URL if we're on the editor route
  const projectIdFromUrl = location.pathname.match(/\/projects\/([^/]+)/)?.[1] || null;

  // Show paywall when timer frozen AND current project not unlocked
  const unlockedIds = new Set(timer?.unlocked_project_ids || []);
  const currentProjectUnlocked = projectIdFromUrl && unlockedIds.has(projectIdFromUrl);
  const showPaywall =
    (frozen && !timer?.is_premium_admin && !currentProjectUnlocked) || forcePaywall;

  return (
    <div className="min-h-screen bg-paper text-ink flex" data-testid="dashboard-layout">
      <div className="hidden lg:flex lg:w-64 lg:flex-shrink-0">
        <Sidebar />
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-ink/30 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-paper shadow-2xl">
            <div className="absolute top-3 right-3 z-10">
              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Cerrar menú"
                className="h-9 w-9 grid place-items-center rounded-full bg-white border border-rule text-ink-soft hover:text-ink"
              >
                <X size={16} />
              </button>
            </div>
            <Sidebar onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-rule bg-paper/85 backdrop-blur-xl">
          <button
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
            data-testid="mobile-menu-btn"
            className="h-9 w-9 grid place-items-center rounded-full hover:bg-paper-2 text-ink"
          >
            <Menu size={18} />
          </button>
          <div className="inline-flex items-baseline gap-0.5">
            <span className="font-serif italic text-xl text-copper">una</span>
            <span className="font-serif text-xl text-ink">TESIS</span>
          </div>
          <span className="w-9" />
        </div>

        <main
          key={location.pathname}
          className="flex-1 min-w-0 animate-fade-up"
          data-testid="dashboard-main"
        >
          <UsageTimerBanner timer={timer} onUpgrade={() => setForcePaywall(true)} />
          {!location.pathname.includes("/projects/") && <InactivityBanner />}
          <Outlet />
        </main>
      </div>

      {needsOnboarding && <OnboardingWizard />}
      {showPaywall && (
        <PaywallModal timer={timer} projectId={projectIdFromUrl} />
      )}
    </div>
  );
}
